import React, { useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RoomContext } from '../context/RoomContext';
import ScrollToTop from '../components/ScrollToTop';
import { FaMapMarkerAlt, FaWifi, FaCoffee, FaParking, FaUtensils, FaGlassCheers } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Reviews from '../pages/Reviews';
import { formatDisplayDate } from '../utils/dateUtils';

const RoomDetails = () => {
  const { rooms, selectedRoomDetails, setSelectedRoomDetails } = useContext(RoomContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const room = rooms.find((room) => room.id === Number(id));

  useEffect(() => {
    console.log("RoomDetails selectedRoomDetails:", selectedRoomDetails);
  }, [selectedRoomDetails]);

  if (!room) {
    return <div>Room not found</div>;
  }

  const handleNavigation = () => {
    const numberOfDays = selectedRoomDetails.numberOfDays || 1;
    const roomPrice = Number(room.price) || 0;
    const priceWithMeal = roomPrice * numberOfDays;

    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      roomType: room.name,
      price: priceWithMeal,
      mealPlan: 'CP',
      mealCost: 0,
      taxes: 0,
      totalAmount: priceWithMeal,
      adults: selectedRoomDetails.adults,
      kids: selectedRoomDetails.kids,
    }));

    setTimeout(() => {
      navigate('/customerdetails');
    }, 100);
  };

  return (
    <article>
      <Helmet>
        <title>{room.name} | Hiwa Boutique Hotel</title>
        <meta name="description" content={`Details and booking for ${room.name}. Enjoy luxury and comfort with our top-notch amenities.`} />
      </Helmet>
      <ScrollToTop />
      <header className="bg-room bg-cover bg-center h-[560px] relative flex justify-center items-center">
        <div className="absolute w-full h-full bg-black/20"></div>
        <h1 className="text-4xl md:text-6xl text-white z-20 font-primary text-center">
          {room.name}
        </h1>
      </header>
      <section className="container mx-auto px-4 md:px-0">
        <div className="flex flex-col lg:flex-row h-full py-12 lg:py-24">
          <div className="w-full lg:w-[60%] px-4 md:px-6">
            <h2 className="h2 text-3xl md:text-4xl">{room.name}</h2>
            <p className="mb-8 text-lg">{room.description}</p>
            <section>
              <h3 className="h3 text-2xl mt-8">Hotel Rules</h3>
              <ul className="mb-6 list-disc list-inside text-lg">
                <li>Quiet hours are observed from 10 PM to 7 AM</li>
                <li>Visitors must depart by 10 PM.</li>
                <li>Please maintain the room in good condition</li>
                <li>Use the in-room safe for storing valuables</li>
                <li>Contact the front desk in case of emergencies</li>
              </ul>
            </section>
            <section>
              <h3 className="h3 text-2xl mt-8">Room Facilities</h3>
              <ul className="mb-6 list-none text-lg">
                <li className="flex items-center">
                  <FaWifi className="text-blue-500 mr-2" /> Wifi
                </li>
                <li className="flex items-center">
                  <FaCoffee className="text-yellow-500 mr-2" /> Coffee
                </li>
                <li className="flex items-center">
                  <FaParking className="text-green-500 mr-2" /> Parking Space
                </li>
                <li className="flex items-center">
                  <FaUtensils className="text-orange-500 mr-2" /> Breakfast
                </li>
                <li className="flex items-center">
                  <FaGlassCheers className="text-purple-500 mr-2" /> Drinks
                </li>
              </ul>
            </section>
          </div>
          <aside className="w-full lg:w-[40%] px-4 md:px-6">
            <ReservationSummary
              room={room}
              handleNavigation={handleNavigation}
            />
          </aside>
        </div>
      </section>
      <section className="bg-gray-10 mb-10">
        <div className="container mx-auto px-2 sm:px-4 max-w-md">
          <Reviews />
        </div>
      </section>
    </article>
  );
};

const ReservationSummary = ({ room, handleNavigation }) => {
  const { selectedRoomDetails } = useContext(RoomContext);

  const totalRoomPrice = room.price * (selectedRoomDetails.numberOfDays || 1);

  return (
    <div className="p-4 border rounded shadow-lg bg-white">
      <h3 className="text-xl font-semibold">{room.name}</h3>
      <div className="my-4">
        <div className="grid grid-cols-2 gap-2">
          <p><strong>Check-In:</strong> {formatDisplayDate(selectedRoomDetails.checkInDate)}</p>
          <p><strong>Check-Out:</strong> {formatDisplayDate(selectedRoomDetails.checkOutDate)}</p>
          <p><strong>Adults:</strong> {selectedRoomDetails.adults || 0}</p>
          <p><strong>Kids:</strong> {selectedRoomDetails.kids || 0}</p>
        </div>
      </div>

      <p className="mb-4 text-gray-700">
        <strong>Meal Plan:</strong> complimentary breakfast
      </p>

      <ul className="list-disc pl-5">
        <li className="text-green-600">Free Cancellation till check-in</li>
        <li className="text-blue-500">Couples are allowed at our Property</li>
      </ul>

      <p className="text-2xl font-bold">
        ₹ {totalRoomPrice}
      </p>

      <button
        className="btn btn-lg btn-primary w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-700"
        onClick={handleNavigation}
      >
        BOOK THIS NOW
      </button>

      <div className="mt-4 border-t pt-4">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <p className="text-lg font-bold text-blue-500">3.9</p>
          </div>
          <div className="ml-4">
            <p className="font-bold text-blue-700">Very Good</p>
          </div>
        </div>

        <div className="flex items-center mt-4">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <FaMapMarkerAlt className="text-blue-500" />
          </div>
          <div className="ml-4">
            <a
              href="https://www.google.com/maps/place/Hiwa+Boutique+Hotel/@17.4076049,78.5986659,17.35z/data=!4m9!3m8!1s0x3bcb9eedbd5adca5:0x21032dc84d31837b!5m2!4m1!1i2!8m2!3d17.40752!4d78.598689!16s%2Fg%2F11x7r9hhfy!5m1!1e1?entry=ttu&g_ep=EgoyMDI1MDMxMi4wIKXMDSoASAFQAw%3D%3D"
              className="flex items-center"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View on Google Maps"
            >
              <span> Om Vihar Colony, Medipally, Canara Nagar, Boduppal, Hyderabad, Telangana 500098</span>
            </a>
            <p className="text-sm text-blue-500 cursor-pointer">
              <a
                href="https://www.google.com/maps/place/Hiwa+Boutique+Hotel/@17.4076049,78.5986659,17.35z/data=!4m9!3m8!1s0x3bcb9eedbd5adca5:0x21032dc84d31837b!5m2!4m1!1i2!8m2!3d17.40752!4d78.598689!16s%2Fg%2F11x7r9hhfy!5m1!1e1?entry=ttu&g_ep=EgoyMDI1MDMxMi4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
              >
                See on Map
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetails;
